// @givmo/compliance — a form signed outside Givmo (CCM-R1-SIGN-OUTSIDE-B; the signing design §6.4;
// frames 84–90, approved as shown, D-CCM-41): the workbench's quiet line and its upload sheet, the
// recorded copy's two cards and its download, and the comparison with the prepared form.
//
// A copy signed outside Givmo is the organization's own record of a version it signed on paper or
// somewhere other than Givmo: never a Givmo signature, and never checked against the prepared form.
// Every view of one says so, in the statement the backend serves on it. The words are
// `compliance-signed-outside-model.ts`'s, every one approved; the signers and the statement are shown
// as served.
//
// Recording one is a Compliance Owner's or a Global Admin's act, the sender's (the server decides,
// and a refusal shows in its own words). The sheet names the version Givmo last recorded for the
// form, the one a Download handed over: a copy executes a version someone could have printed, so
// opening the sheet never records a new one.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Dialog,
  ErrorMessage,
  Field,
  Icon,
  Sheet,
  cn,
  testIdProp,
} from '@givmo/design-system';
import { Download, FileText, Plus, X } from 'lucide-react';
import { ArtifactUpload } from './artifact-upload';
import { useCompliance } from './compliance-context';
import { ComplianceDateField } from './compliance-date-field';
import {
  displayArtifactSize,
  localDayOf,
  serverMessage,
  type ComplianceArtifact,
} from './compliance-decode';
import type { DocumentVersion, FormPreparation } from './compliance-preparation-decode';
import { CANCEL, CHANGE, SAVE, THE_FORM } from './compliance-preparation-model';
import { filingDocumentQueryKey, versionRenditionQueryKey } from './compliance-queries';
import { TITLE_AS_STATED_LABEL, browserTimeZone, versionLine } from './compliance-sending-model';
import {
  ADD_ANOTHER_SIGNER,
  CHOOSE_THE_PDF,
  COMPARE_LEAD,
  COMPARE_WITH_PREPARED,
  DATE_SIGNED,
  DOWNLOAD_THE_SIGNED_COPY,
  EMPTY_SIGNER,
  MAX_SIGNERS,
  PREPARED_HEADING,
  SIGNED_COPY_HEADING,
  SIGNED_ON_PAPER,
  SIGNED_OUTSIDE_STATEMENT,
  SIGNER_NAME,
  SIGNER_NAME_MAX_LENGTH,
  SIGNER_TITLE_MAX_LENGTH,
  THE_SIGNED_COPY,
  UPLOAD_LEAD,
  UPLOAD_THE_SIGNED_COPY,
  WHO_SIGNED,
  comparedPages,
  executedCopyBody,
  lastSignedOn,
  pageChoice,
  readyToSave,
  signedByStatedLine,
  signedOutsideOnLine,
  standingCopyOf,
  type SignerRow,
} from './compliance-signed-outside-model';
import {
  decodeFilingDocumentVersions,
  decodeVersionRendition,
  type RenditionPage,
} from './compliance-signing-decode';
import { useOpenVersion } from './signing-shared';

const PREFIX = 'compliance-outside';

/** Built: an act that failed with no words of its own (the package's one line for it). */
const RECORD_FAILED = 'We couldn’t record that. Please try again.';

/** The vault's upload, held to a PDF: a copy is a filing document, and only a PDF is one. */
const PDF_ONLY = 'application/pdf,.pdf';

// ── Reads ──────────────────────────────────────────────────────────────────────────────────────

/** The copy signed outside Givmo that stands for the form's recorded version, and its pages. */
export interface SignedOutside {
  copy: DocumentVersion;
  /** The version it executes: the form as Givmo last recorded it. */
  prepared: DocumentVersion;
  /** The copy's own drawn pages, and the prepared version's (the comparison's two sides). */
  pages: RenditionPage[];
  preparedPages: RenditionPage[];
  /** Either side's pages are still being read: not yet "no picture". */
  pagesPending: boolean;
}

/**
 * The form's recorded copy, read off its document's versions (every version, oldest first), with
 * the copy's pages and the prepared version's when `withPages` (the workbench; the panel reads the
 * copy alone). `pending` while the document is read.
 */
export function useSignedOutside(
  preparation: FormPreparation | undefined,
  { withPages = true }: { withPages?: boolean } = {},
): { outside?: SignedOutside; pending: boolean } {
  const { orgRef, isReady, moduleStanding, operations } = useCompliance();
  const ref = isReady && moduleStanding === 'available' ? orgRef : null;
  const documentId = preparation?.documentId;
  const prepared = preparation?.latestVersion;
  const document = useQuery({
    queryKey: filingDocumentQueryKey(ref, documentId),
    queryFn: () => operations.getFilingDocument(ref ?? '', documentId ?? ''),
    enabled: ref !== null && documentId !== undefined && prepared !== undefined,
  });
  const copy = standingCopyOf(decodeFilingDocumentVersions(document.data), prepared?.versionId);
  const pagesWanted = withPages && ref !== null && copy !== undefined && prepared !== undefined;
  const copyPages = useQuery({
    queryKey: versionRenditionQueryKey(ref, documentId, copy?.versionId),
    queryFn: () =>
      operations.getDocumentVersionRendition(ref ?? '', documentId ?? '', copy?.versionId ?? ''),
    enabled: pagesWanted,
  });
  const preparedPages = useQuery({
    queryKey: versionRenditionQueryKey(ref, documentId, prepared?.versionId),
    queryFn: () =>
      operations.getDocumentVersionRendition(
        ref ?? '',
        documentId ?? '',
        prepared?.versionId ?? '',
      ),
    enabled: pagesWanted,
  });
  return {
    ...(copy !== undefined && prepared !== undefined
      ? {
          outside: {
            copy,
            prepared,
            pages: decodeVersionRendition(copyPages.data),
            preparedPages: decodeVersionRendition(preparedPages.data),
            pagesPending: pagesWanted && (copyPages.isPending || preparedPages.isPending),
          },
        }
      : {}),
    pending: documentId !== undefined && prepared !== undefined && document.isPending,
  };
}

// ── The workbench's quiet line (frame 84) ──────────────────────────────────────────────────────

/** W10 and its link W11, under the page column's note. */
export function PaperRouteLine({ onUpload }: { onUpload: () => void }) {
  return (
    <p className="text-s12 leading-h15 text-text-tertiary" {...testIdProp(`${PREFIX}-line`)}>
      {SIGNED_ON_PAPER}{' '}
      <button
        type="button"
        onClick={onUpload}
        className="rounded-xs text-s12 text-text-secondary underline underline-offset-2 hover:text-text-strong"
        {...testIdProp(`${PREFIX}-upload`)}
      >
        {UPLOAD_THE_SIGNED_COPY}
      </button>
    </p>
  );
}

// ── The sheet (frames 85, 86) ──────────────────────────────────────────────────────────────────

/** The viewer's own day, `YYYY-MM-DD`: the latest date a person can have signed on. */
function todayHere(): string {
  return localDayOf(new Date().toISOString()) ?? '';
}

export function UploadSignedCopySheet({
  open,
  onClose,
  version,
  formLine,
  keepsTimeZone,
  onRecorded,
}: {
  open: boolean;
  onClose: () => void;
  /** The version the copy executes: the form as Givmo last recorded it. */
  version: DocumentVersion | undefined;
  /** "Annual Update of Registration Form · fiscal year ended June 30, 2026" (the send sheet's). */
  formLine: string;
  /**
   * Whether the server keeps the uploader's time zone with the copy: it serves the preparation
   * read's `current_execution`, which came with it (CCM-R1-SIGN-OUTSIDE-SEND). A server that
   * predates both refuses the act's unknown field, so the zone is sent only to one that keeps it.
   */
  keepsTimeZone: boolean;
  /** The document as the act answered it, the copy among its versions. */
  onRecorded: (answer: unknown) => void;
}) {
  return (
    <Sheet.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {open && version !== undefined && (
        <Sheet.Content size="lg" testId={`${PREFIX}-sheet`} initialFocus={false}>
          <UploadForm
            version={version}
            formLine={formLine}
            keepsTimeZone={keepsTimeZone}
            onClose={onClose}
            onRecorded={onRecorded}
          />
        </Sheet.Content>
      )}
    </Sheet.Root>
  );
}

function UploadForm({
  version,
  formLine,
  keepsTimeZone,
  onClose,
  onRecorded,
}: {
  version: DocumentVersion;
  formLine: string;
  keepsTimeZone: boolean;
  onClose: () => void;
  onRecorded: (answer: unknown) => void;
}) {
  const { orgRef, operations } = useCompliance();
  const [file, setFile] = useState<ComplianceArtifact | null>(null);
  const [rows, setRows] = useState<SignerRow[]>([EMPTY_SIGNER]);
  const today = useMemo(todayHere, []);
  const ready = readyToSave(file !== null, rows, today);
  const record = useMutation({
    mutationFn: () => {
      if (file === null) throw new Error(RECORD_FAILED);
      return operations.recordExecutedCopy(
        orgRef ?? '',
        version.documentId,
        version.versionId,
        executedCopyBody(file.artifactId, rows, keepsTimeZone ? browserTimeZone() : undefined),
      );
    },
    onSuccess: onRecorded,
  });
  const setRow = (index: number, next: Partial<SignerRow>) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...next } : row)));

  return (
    <>
      <Sheet.Header>
        <Sheet.Title className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
          {UPLOAD_THE_SIGNED_COPY}
        </Sheet.Title>
      </Sheet.Header>
      <Sheet.Body className="flex flex-col gap-28">
        <p className="text-s13 leading-h15 text-text-secondary" {...testIdProp(`${PREFIX}-lead`)}>
          {UPLOAD_LEAD}
        </p>
        <section className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-form`)}>
          <h2 className="text-s14 font-w600 text-text-strong">{THE_FORM}</h2>
          <div className="flex items-start gap-12 rounded-lg border border-border-subtle px-16 py-12">
            <Icon icon={FileText} size={18} className="mt-2 shrink-0 text-icon-muted" aria-hidden />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <p className="text-s14 text-text">{formLine}</p>
              <p className="text-s12 text-text-tertiary" {...testIdProp(`${PREFIX}-version`)}>
                {versionLine(version.createdAt, version.versionNumber ?? 1, version.pageCount ?? 0)}
              </p>
            </div>
          </div>
        </section>
        <section className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-file`)}>
          <h2 className="text-s14 font-w600 text-text-strong">{THE_SIGNED_COPY}</h2>
          {file === null ? (
            <ArtifactUpload
              label={CHOOSE_THE_PDF}
              accept={PDF_ONLY}
              onUploaded={setFile}
              testId={`${PREFIX}-choose`}
            />
          ) : (
            <div className="flex items-start gap-12 rounded-lg border border-border-subtle px-16 py-12">
              <Icon
                icon={FileText}
                size={18}
                className="mt-2 shrink-0 text-icon-muted"
                aria-hidden
              />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <p className="truncate text-s14 text-text" {...testIdProp(`${PREFIX}-file-name`)}>
                  {file.fileName}
                </p>
                {file.byteSize !== undefined && (
                  <p className="text-s12 text-text-tertiary">
                    {displayArtifactSize(file.byteSize)}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="rounded-xs text-s13 text-text-secondary underline underline-offset-2 hover:text-text-strong"
                {...testIdProp(`${PREFIX}-file-change`)}
              >
                {CHANGE}
              </button>
            </div>
          )}
        </section>
        <section className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-who`)}>
          <h2 className="text-s14 font-w600 text-text-strong">{WHO_SIGNED}</h2>
          {rows.map((row, index) => (
            <div
              key={index}
              className="grid grid-cols-1 gap-12 rounded-lg border border-border-subtle p-16 sm:grid-cols-2"
              {...testIdProp(`${PREFIX}-signer-${index}`)}
            >
              <Field
                label={SIGNER_NAME}
                value={row.name}
                maxLength={SIGNER_NAME_MAX_LENGTH}
                onChange={(event) => setRow(index, { name: event.target.value })}
                testId={`${PREFIX}-name-${index}`}
              />
              <Field
                label={TITLE_AS_STATED_LABEL}
                value={row.capacity}
                maxLength={SIGNER_TITLE_MAX_LENGTH}
                onChange={(event) => setRow(index, { capacity: event.target.value })}
                testId={`${PREFIX}-title-${index}`}
              />
              <ComplianceDateField
                label={DATE_SIGNED}
                value={row.signedOn}
                latest={today}
                onChange={(iso) => setRow(index, { signedOn: iso })}
                testId={`${PREFIX}-date-${index}`}
              />
            </div>
          ))}
          {rows.length < MAX_SIGNERS && (
            <span>
              <Button
                appearance="saas"
                tone="ghost"
                size="sm"
                leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
                onClick={() => setRows((current) => [...current, EMPTY_SIGNER])}
                testId={`${PREFIX}-add-signer`}
              >
                {ADD_ANOTHER_SIGNER}
              </Button>
            </span>
          )}
        </section>
        <p className="text-s13 leading-h15 text-text" {...testIdProp(`${PREFIX}-statement`)}>
          {SIGNED_OUTSIDE_STATEMENT}
        </p>
      </Sheet.Body>
      <Sheet.Footer>
        <div className="flex flex-col items-end gap-8">
          {record.error !== null && record.error !== undefined && (
            <ErrorMessage testId={`${PREFIX}-failure`}>
              {serverMessage(record.error, RECORD_FAILED)}
            </ErrorMessage>
          )}
          <div className="flex gap-8">
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              disabled={!ready}
              loading={record.isPending}
              onClick={() => record.mutate()}
              testId={`${PREFIX}-save`}
            >
              {SAVE}
            </Button>
            <Button
              appearance="saas"
              tone="ghost"
              size="md"
              onClick={onClose}
              disabled={record.isPending}
              testId={`${PREFIX}-cancel`}
            >
              {CANCEL}
            </Button>
          </div>
        </div>
      </Sheet.Footer>
    </>
  );
}

/** Once a copy is recorded: the lists and the document read again, so every surface shows it. */
export function useAfterCopyRecorded() {
  const { orgRef } = useCompliance();
  const queryClient = useQueryClient();
  return (answer: unknown, documentId: string) => {
    queryClient.setQueryData(filingDocumentQueryKey(orgRef, documentId), answer);
    void queryClient.invalidateQueries({ queryKey: ['compliance'] });
  };
}

// ── The recorded copy (frame 87) ───────────────────────────────────────────────────────────────

export function SignedOutsideCards({
  outside,
  ledger,
  onCompare,
}: {
  outside: SignedOutside;
  /** The panel's four lines for the filing: label and value (Signed is W15). */
  ledger: readonly (readonly [string, string])[];
  onCompare: () => void;
}) {
  const execution = outside.copy.execution;
  const signedOn = lastSignedOn(outside.copy);
  return (
    <div className="grid gap-16 lg:grid-cols-2" {...testIdProp(PREFIX)}>
      <Card className="flex flex-col gap-14 px-20 py-16" testId={`${PREFIX}-copy`}>
        <div className="flex flex-col gap-2">
          <h2 className="text-s16 font-w600 text-text-strong">{THE_SIGNED_COPY}</h2>
          {signedOn !== undefined && (
            <p className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-signed-on`)}>
              {signedOutsideOnLine(signedOn)}
            </p>
          )}
        </div>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-20 gap-y-8 text-s13">
          {ledger.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-text-tertiary">{label}</dt>
              <dd className="text-text">{value}</dd>
            </div>
          ))}
        </dl>
      </Card>
      <Card className="flex flex-col gap-12 px-20 py-16" testId={`${PREFIX}-who-signed`}>
        <h2 className="text-s16 font-w600 text-text-strong">{WHO_SIGNED}</h2>
        <ul className="flex flex-col gap-8 text-s13 leading-h15 text-text">
          {(execution?.signers ?? []).map((signer, index) => (
            <li key={index} {...testIdProp(`${PREFIX}-signed-by`)}>
              {signedByStatedLine(signer)}
            </li>
          ))}
        </ul>
        {execution !== undefined && (
          <p
            className="text-s12 leading-h15 text-text-tertiary"
            {...testIdProp(`${PREFIX}-record-statement`)}
          >
            {execution.statement}
          </p>
        )}
        <div className="flex flex-wrap gap-8 pt-2">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={onCompare}
            testId={`${PREFIX}-compare`}
          >
            {COMPARE_WITH_PREPARED}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/** The header's download of the copy (frame 87): its exact bytes, under the name frozen with it. */
export function DownloadSignedCopyButton({ outside }: { outside: SignedOutside }) {
  const openVersion = useOpenVersion();
  return (
    <Button
      appearance="saas"
      tone="secondary"
      size="md"
      leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
      onClick={() => void openVersion(outside.copy.documentId, outside.copy.versionId)}
      testId={`${PREFIX}-download`}
    >
      {DOWNLOAD_THE_SIGNED_COPY}
    </Button>
  );
}

// ── The comparison (frame 88) ──────────────────────────────────────────────────────────────────

export function CompareSignedCopyDialog({
  outside,
  open,
  firstPage,
  onClose,
}: {
  outside: SignedOutside;
  open: boolean;
  /** The page it opens on: the form's signature page. */
  firstPage: number;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {open && (
        <Dialog.Content
          width={1120}
          className="flex flex-col gap-20"
          testId={`${PREFIX}-compare-dialog`}
        >
          <Comparison outside={outside} firstPage={firstPage} />
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}

function Comparison({ outside, firstPage }: { outside: SignedOutside; firstPage: number }) {
  const pages = comparedPages(outside.preparedPages.length, outside.pages.length);
  const [chosen, setChosen] = useState<number | undefined>(undefined);
  const page = chosen ?? (pages.includes(firstPage) ? firstPage : (pages.at(-1) ?? 1));
  const sides = [
    [PREPARED_HEADING, outside.preparedPages.find((drawn) => drawn.page === page), 'prepared'],
    [SIGNED_COPY_HEADING, outside.pages.find((drawn) => drawn.page === page), 'copy'],
  ] as const;
  return (
    <>
      <div className="flex items-start justify-between gap-16">
        <div className="flex flex-col gap-6">
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {COMPARE_WITH_PREPARED}
          </Dialog.Title>
          <Dialog.Description className="text-s13 leading-h15 text-text-secondary">
            {COMPARE_LEAD}
          </Dialog.Description>
        </div>
        <Dialog.Close
          className="rounded-all100 text-gray80 outline-none focus-visible:shadow-focus-glow"
          aria-label="Close"
          {...testIdProp(`${PREFIX}-compare-close`)}
        >
          <Icon icon={X} size={24} aria-hidden />
        </Dialog.Close>
      </div>
      <div className="flex flex-wrap gap-12" {...testIdProp(`${PREFIX}-compare-pages`)}>
        {pages.map((number) => (
          <button
            key={number}
            type="button"
            aria-current={number === page ? 'page' : undefined}
            onClick={() => setChosen(number)}
            className={cn(
              'rounded-xs text-s13',
              number === page
                ? 'font-w600 text-text-strong'
                : 'text-text-secondary underline underline-offset-2 hover:text-text-strong',
            )}
            {...testIdProp(`${PREFIX}-compare-page-${number}`)}
          >
            {pageChoice(number)}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-20 sm:grid-cols-2">
        {sides.map(([heading, drawn, side]) => (
          <div
            key={side}
            className="flex min-w-0 flex-col gap-8"
            {...testIdProp(`${PREFIX}-compare-${side}`)}
          >
            <h3 className="text-s14 font-w600 text-text-strong">{heading}</h3>
            <div className="overflow-hidden rounded-lg border border-border-subtle bg-white">
              {drawn !== undefined ? (
                <img
                  src={drawn.imageUrl}
                  alt=""
                  width={drawn.width}
                  height={drawn.height}
                  className="block h-auto w-full select-none"
                  draggable={false}
                  {...testIdProp(`${PREFIX}-compare-${side}-image`)}
                />
              ) : (
                // A side with no such page (or not drawn yet) is an empty frame of the page's shape.
                <div className="aspect-[612/792] w-full" />
              )}
            </div>
          </div>
        ))}
      </div>
      <p
        className="text-s12 leading-h15 text-text-tertiary"
        {...testIdProp(`${PREFIX}-compare-statement`)}
      >
        {outside.copy.execution?.statement ?? SIGNED_OUTSIDE_STATEMENT}
      </p>
    </>
  );
}
